import { useCallback } from "react";
import { cloneDeep, debounce, get } from "lodash";
import { useTheme } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { Select, Box, Toggle } from "orcs-design-system";
import {
  useFilters,
  useApplyFilters,
} from "src/contexts/filterAndFte/FilterAndFteContext";
import { DEBOUNCE_SEARCH_TIME } from "src/consts/global";
import formatGroupLabel from "./formatGroupLabel";
import formatOptionLabel from "./formatOptionLabel";
import { updateFilterValues } from "./filterConfigs";
import type {
  FilterToggleConfig,
  GroupedFilterOption,
  OptionLabelType,
} from "./filterConfigs";
import type { FilterOption } from "../../context/types";

type FilterSelectProps = {
  onInputChange?: (...args: unknown[]) => void;
  isSearching?: boolean;
  options: FilterOption[] | GroupedFilterOption[];
  valueKeys: string[];
  toggles?: FilterToggleConfig[];
  name: string;
  optionLabelType?: OptionLabelType;
  filterOption?: boolean;
  backspaceRemovesValue?: boolean;
  isMulti?: boolean;
  [key: string]: unknown;
};

// react-select's style-function shape isn't typed here; orcs-design-system
// re-exports it untyped, so these stay loose rather than fighting the library.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectStyleFn = (provided: Record<string, unknown>, state: any) => any;

type SelectStyles = Record<string, SelectStyleFn> & {
  multiValueLabel: SelectStyleFn;
  multiValueRemove: SelectStyleFn;
};

// Selected values default to the full-size ORCS chip, which crowds the narrow
// filter sidebar. Tag options bring their own chip, so their label is bare.
const compactSelectedValueStyles =
  ({ radius, hasOwnChip }: { radius: string; hasOwnChip: boolean }) =>
  (styles: SelectStyles): SelectStyles => ({
    ...styles,
    multiValueLabel: (provided, state) => ({
      ...styles.multiValueLabel(provided, state),
      padding: hasOwnChip ? "0 !important" : "2px 4px 2px 6px !important",
      borderRadius: state.data.isFixed ? radius : `${radius} 0 0 ${radius}`,
    }),
    multiValueRemove: (provided, state) => ({
      ...styles.multiValueRemove(provided, state),
      padding: "2px 3px 2px 2px",
      minWidth: "18px",
      minHeight: "18px",
      borderRadius: `0 ${radius} ${radius} 0`,
    }),
  });

const FilterSelect = (props: FilterSelectProps) => {
  const {
    onInputChange,
    isSearching = false,
    options,
    valueKeys,
    toggles = [],
    name,
    optionLabelType,
    filterOption = false,
    backspaceRemovesValue = false,
    isMulti = true,
    ...selectProps
  } = props;

  const filters = useFilters();
  const theme = useTheme();

  const applyFilters = useApplyFilters();
  const rawSingleValue = get(filters, valueKeys[0] ?? "", []);
  const filterValue = isMulti
    ? valueKeys.map((key) => get(filters, key, [])).flat()
    : Array.isArray(rawSingleValue)
      ? (rawSingleValue[0] ?? null)
      : (rawSingleValue ?? null);

  const onFilterValueChange = useCallback(
    (values: unknown) => {
      const updatedFilters = updateFilterValues(
        filters,
        name,
        valueKeys,
        values
      );
      applyFilters(updatedFilters);
    },
    [filters, name, applyFilters, valueKeys]
  );

  const onSelectGroupLabel = useCallback(
    (key: string | undefined, value: unknown) => {
      const currentFilters = cloneDeep(filters);
      // `filters` comes from the untyped JS `useFilters()` (FilterAndFteContext.js),
      // so `currentFilters` is really `any` here - there's no real declared shape to
      // reference instead of this cast. It only claims enough structure to call
      // `unshift` on the value at `key`, which is how this filters object is used
      // throughout the sidebar.
      if (key) {
        (currentFilters as Record<string, unknown[]>)[key]?.unshift(value);
      }
      applyFilters(currentFilters);
    },
    [filters, applyFilters]
  );

  const select = (
    <Select
      placeholder="Search or select"
      data-testid={selectProps["inputId"]}
      {...selectProps}
      options={options}
      value={filterValue}
      filterOption={filterOption}
      isMulti={isMulti}
      onChange={(value: unknown) =>
        onFilterValueChange(isMulti ? value : value ? [value] : [])
      }
      onInputChange={
        onInputChange && debounce(onInputChange, DEBOUNCE_SEARCH_TIME)
      }
      backspaceRemovesValue={backspaceRemovesValue}
      isLoading={isSearching}
      updateStyles={compactSelectedValueStyles({
        radius: themeGet("radii.2")({ theme }) as string,
        hasOwnChip: optionLabelType === "tagOption",
      })}
      formatOptionLabel={
        optionLabelType ? formatOptionLabel[optionLabelType] : undefined
      }
      formatGroupLabel={(data: Parameters<typeof formatGroupLabel>[0]) =>
        formatGroupLabel(data, onSelectGroupLabel)
      }
    />
  );

  return (
    <Box mb="r">
      {select}
      {toggles.length > 0 && (
        <Box mt="r">
          {toggles.map((toggle) => (
            <Toggle
              key={toggle.id}
              small
              id={toggle.id}
              label={toggle.label}
              className={`guided-tour-filter-toggle-${toggle.id}`}
              checked={get(filters, toggle.valueKey)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onFilterValueChange({
                  value: e.target.checked,
                  valueKey: toggle.valueKey,
                });
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FilterSelect;
